// Regras puras dos grupos do quadro: crescer para caber um cartão e empurrar os vizinhos (RQ-EST-005).

import type { Ponto } from './camera';

export type Tamanho = { largura: number; altura: number };
export type Retangulo = Ponto & Tamanho;
export type RetanguloGrupo = Retangulo & { uuid: string };

/** Espaço livre mínimo entre dois grupos. */
export const ESPACO_ENTRE_GRUPOS = 24;
/** Folga entre o cartão e a borda do grupo; no topo, a altura do cabeçalho (ícone, título, contador). */
export const MARGEM_INTERNA = { lateral: 16, topo: 76, base: 16 };
const LIMITE_PASSOS = 500;

/** Traz o cartão para dentro do grupo (sem invadir o cabeçalho) e faz o grupo crescer, se preciso, para caber. */
export function acomodarCartao<G extends Retangulo>(grupo: G, posicao: Ponto, cartao: Tamanho): { posicao: Ponto; grupo: G } {
  const x = Math.max(MARGEM_INTERNA.lateral, posicao.x);
  const y = Math.max(MARGEM_INTERNA.topo, posicao.y);
  const largura = Math.max(grupo.largura, x + cartao.largura + MARGEM_INTERNA.lateral);
  const altura = Math.max(grupo.altura, y + cartao.altura + MARGEM_INTERNA.base);
  const cresceu = largura !== grupo.largura || altura !== grupo.altura;
  return { posicao: { x, y }, grupo: cresceu ? { ...grupo, largura, altura } : grupo };
}

/** Dois grupos colidem quando ficam a menos de `espaco` um do outro. */
export function colidem(a: Retangulo, b: Retangulo, espaco = ESPACO_ENTRE_GRUPOS) {
  return a.x < b.x + b.largura + espaco && b.x < a.x + a.largura + espaco && a.y < b.y + b.altura + espaco && b.y < a.y + a.altura + espaco;
}

/** Menor deslocamento de `b` para longe de `a`: para a direita ou para baixo (o grupo cresce para esses lados). */
function afastar(a: Retangulo, b: Retangulo, espaco: number): Ponto {
  const direita = a.x + a.largura + espaco - b.x;
  const baixo = a.y + a.altura + espaco - b.y;
  return direita <= baixo ? { x: b.x + direita, y: b.y } : { x: b.x, y: b.y + baixo };
}

/** Afasta de `empurrador` cada grupo que colide com ele (menos o intocável); devolve os que foram empurrados. */
function empurrar<G extends RetanguloGrupo>(empurrador: G, grupos: G[], intocavel: G, espaco: number): G[] {
  const empurrados = grupos.filter((g) => g !== intocavel && g !== empurrador && colidem(empurrador, g, espaco));
  for (const g of empurrados) Object.assign(g, afastar(empurrador, g, espaco));
  return empurrados;
}

/**
 * Depois que o grupo `fixoUuid` cresceu, empurra quem passou a colidir com ele, em cascata, sempre pelo menor
 * deslocamento. O grupo que cresceu nunca se move. Devolve só os grupos que mudaram de lugar, já na nova posição.
 */
export function acomodarGrupos<G extends RetanguloGrupo>(grupos: G[], fixoUuid: string, espaco = ESPACO_ENTRE_GRUPOS): G[] {
  const atuais = new Map(grupos.map((g) => [g.uuid, { ...g }]));
  const fixo = atuais.get(fixoUuid);
  if (!fixo) return [];
  const fila = [fixo];
  const movidos = new Set<string>();
  for (let passo = 0; fila.length > 0 && passo < LIMITE_PASSOS; passo++) {
    const empurrador = fila.shift()!;
    // Um grupo empurrado em cascata pode ter ido parar sobre o que cresceu: este o empurra de volta.
    if (empurrador !== fixo && colidem(fixo, empurrador, espaco)) Object.assign(empurrador, afastar(fixo, empurrador, espaco));
    const empurrados = empurrar(empurrador, [...atuais.values()], fixo, espaco);
    for (const g of empurrados) movidos.add(g.uuid);
    fila.push(...empurrados);
  }
  return grupos.filter((g) => movidos.has(g.uuid)).map((g) => atuais.get(g.uuid)!);
}

/**
 * RQ-EST-005 completo: acomoda o cartão no grupo que o recebe; se o grupo cresceu, empurra os vizinhos.
 * Devolve a posição final do cartão (relativa ao grupo) e os grupos que mudaram: o que cresceu e os empurrados.
 */
export function receberCartao<G extends RetanguloGrupo>(grupos: G[], grupoUuid: string, posicao: Ponto, cartao: Tamanho) {
  const grupo = grupos.find((g) => g.uuid === grupoUuid);
  if (!grupo) return { posicao, alterados: [] as G[] };
  const acomodado = acomodarCartao(grupo, posicao, cartao);
  if (acomodado.grupo === grupo) return { posicao: acomodado.posicao, alterados: [] as G[] };
  const comCrescido = grupos.map((g) => (g === grupo ? acomodado.grupo : g));
  return { posicao: acomodado.posicao, alterados: [acomodado.grupo, ...acomodarGrupos(comCrescido, grupoUuid)] };
}

/** Lugar livre para um cartão que chega ao grupo sem posição escolhida: abaixo dos cartões que já estão lá. */
export function proximaPosicaoLivre(cartoes: Retangulo[]): Ponto {
  const fundo = Math.max(MARGEM_INTERNA.topo, ...cartoes.map((c) => c.y + c.altura + MARGEM_INTERNA.base));
  return { x: MARGEM_INTERNA.lateral + 12, y: fundo };
}
